<template>
  <div
    v-tooltip.top="{
      content: getLegend,
      container: 'body',
      boundariesElement: 'body',
      classes: ['graph-legend-tooltip no-padding'],
      delay: { show: 1000, hide: 0 }
    }"
  >
    <div v-if="isLoaded && !hasError && hasGraphData" class="compare-chart-legend d-flex justify-content-between mt-3">
      <p :title="`${publicationsMatched}/${publicationsTotal} matching publications`" class="d-flex t-overflow">
        <span class="t-600 t-xxxlr bd-legend-primary">{{ publicationsMatched | formatNumberDecimal }}</span>
        <span class="t-xs bd-legend-primary--soft t-overflow">/{{ publicationsTotal | formatNumberDecimal }} Publications</span>
      </p>
      <p :title="`${citationsMatched}/${citationsTotal} matching citations`" class="d-flex t-overflow">
        <span class="t-600 t-xxxlr bd-legend-secondary">{{ citationsMatched | formatNumberDecimal }}</span>
        <span class="t-xs bd-legend-secondary--soft t-overflow">/{{ citationsTotal | formatNumberDecimal }} Citations</span>
      </p>
    </div>
    <div v-else class="compare-chart-legend d-flex justify-content-between mt-3">
      <p :title="`-/- matching publications`" class="d-flex t-overflow">
        <span class="t-600 t-xxxlr bd-legend-primary">-</span>
        <span class="t-xs bd-legend-primary--soft t-overflow">/- Publications</span>
      </p>
      <p :title="`-/- matching citations`" class="d-flex t-overflow">
        <span class="t-600 t-xxxlr bd-legend-secondary">-</span>
        <span class="t-xs bd-legend-secondary--soft t-overflow">/- Citations</span>
      </p>
    </div>

    <div v-if="isLoaded && hasGraphData" class="small-chart ratio-3-2">
      <div :id="chartId" class="ratio-content" />
    </div>

    <div v-if="isLoaded && hasError" class="small-chart ratio-3-2 d-flex align-items-center">
      <p class="t-sm t-secondary t-500 mb-0 ml-4">
        Could not fetch publications.
      </p>
    </div>
    <div v-else-if="isLoaded && !hasGraphData" class="small-chart ratio-3-2 d-flex align-items-center">
      <p class="t-sm t-secondary t-500 mb-0 ml-4">
        Publications available soon.
      </p>
    </div>
    <div v-if="!isLoaded" class="small-chart ratio-3-2 d-flex align-items-center">
      <spinner />
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/shared/Spinner'
import helpers from './helpers'

import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'

export default {
  name: 'comparisonChart',
  mixins: [helpers],
  components: {
    Spinner
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false
    },
    graphData: {
      type: Object,
      required: false
    },
    yearsToShow: {
      type: Number,
      default: 16,
      required: false
    },
    hideLegendTooltip: {
      type: Boolean,
      default: false,
      required: false
    },
    hasError: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      citationsMatched: 0,
      publicationsMatched: 0,
      hasGraphData: false
    }
  },
  mounted () {
    this.$nextTick(() => this.parseGraphData())
  },
  watch: {
    graphData (newVal, oldVal) {
      if (newVal !== oldVal && newVal) this.parseGraphData()
    }
  },
  computed: {
    citationsTotal () {
      if (this.graphData && this.graphData.citations && this.graphData.citations.length > 0) {
        let total = 0
        this.graphData.citations.forEach(dataPoint => {
          if (dataPoint.total) total += dataPoint.total
        })
        return total
      }
      return 0
    },
    publicationsTotal () {
      if (this.graphData && this.graphData.publications && this.graphData.publications.length > 0) {
        let total = 0
        this.graphData.publications.forEach(dataPoint => {
          if (dataPoint.total) total += dataPoint.total
        })
        return total
      }
      return 0
    },
    getLegend () {
      if (!this.hasGraphData || this.hideLegendTooltip) return ''
      return `
        <div class="t-primary legend-tooltip-wrapper">
          <div class="d-flex align-items-center">
            <div class="legend-tooltip-square mr-2" style="background-color: rgba(67,174,197,0.7);"></div>
            <span class="t-compact t-xs">Publications matching your query</span>
          </div>
          <div class="d-flex align-items-center">
            <div class="legend-tooltip-square mr-2" style="background-color: rgba(67,174,197,0.1);"></div>
            <span class="t-compact t-xs">Publications</span>
          </div>
          <div class="d-flex align-items-center">
            <div class="legend-tooltip-square mr-2" style="background-color: #EFA9BB;"></div>
            <span class="t-compact t-xs">Citations for publications matching your query</span>
          </div>
          <div class="d-flex align-items-center mb-1">
            <div class="legend-tooltip-square mr-2" style="background-color: rgba(247, 188, 203, 0.35);"></div>
            <span class="t-compact t-xs">Citations</span>
          </div>
          <div class="d-flex">
            <i class="legend-tooltip-square t-secondary pl-1 mr-2 fa fa-info-circle"></i>
            <span class="t-compact t-xs">Citations per year is the sum of citations over time that publications from that year have received</span>
          </div>
        </div>
      `
    }
  },
  methods: {
    // Chart rendering
    renderGraph (publications, publicationsTotal, citations, citationsTotal) {
      const root = this.initRoot()

      this.setAxisStyle(root)
      // Init standard XY chart element
      const chart = this.initChartElement(root)
      // Generate standard yAxis and attach it to the root+chart
      const [yAxis, yAxisInverted] = this.generateYAxis({
        root,
        chart,
        withInvertedAxis: true
      })

      const xAxis = this.generateXAxis({
        root,
        chart,
        minGridDistance: 50,
        categoryField: 'year'
      })
      xAxis.data.setAll(publications)

      const publicationsSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Publications',
          valueYField: 'y',
          categoryXField: 'year',
          xAxis: xAxis,
          yAxis: yAxis,
          fill: am5.color(0x43aec5),
          clustered: false
        })
      )
      publicationsSeries.data.setAll(publicationsTotal)

      publicationsSeries.columns.template.setAll({
        fill: am5.color(0x43aec5),
        fillOpacity: 0.3,
        visible: true,
        width: am5.percent(80)
      })

      const matchedPublicationsSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Matched Publications',
          valueYField: 'y',
          categoryXField: 'year',
          xAxis: xAxis,
          yAxis: yAxis,
          fill: am5.color(0x43aec5),
          clustered: false
        })
      )
      matchedPublicationsSeries.data.setAll(publications)

      matchedPublicationsSeries.columns.template.setAll({
        fill: am5.color(0x43aec5),
        fillOpacity: 0.7,
        visible: true,
        width: am5.percent(80)
      })

      const matchedCitationsSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: 'Matched Citations',
          valueYField: 'y',
          categoryXField: 'year',
          xAxis: xAxis,
          yAxis: yAxisInverted,
          stroke: am5.color(0xefa9bb)
        })
      )
      matchedCitationsSeries.data.setAll(citations)
      matchedCitationsSeries.strokes.template.setAll({
        strokeWidth: 2
      })

      const citationsSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: 'Citations',
          valueYField: 'y',
          categoryXField: 'year',
          xAxis: xAxis,
          yAxis: yAxisInverted,
          stroke: am5.color(0xefa9bb)
        })
      )
      citationsSeries.data.setAll(citationsTotal)
      citationsSeries.strokes.template.setAll({
        strokeWidth: 2,
        strokeOpacity: 0.4
      })
    },
    // Data stuff
    parseGraphData () {
      const graphData = this.graphData
      this.hasGraphData = false

      if (graphData &&
        (
          (graphData.publications && graphData.publications.length > 0) ||
          (graphData.citations && graphData.citations.length > 0)
        )
      ) {
        const [publications, publicationsTotal, citations, citationsTotal] = this.syncCharts(
          this.formatGraphData(graphData.publications, true),
          this.formatGraphData(graphData.publications),
          this.formatGraphData(graphData.citations, true, true),
          this.formatGraphData(graphData.citations, false, true)
        )

        // Do graph rendering shizzle
        this.hasGraphData = true

        this.$nextTick(() => {
          this.renderGraph(publications, publicationsTotal, citations, citationsTotal)
        })
      }
    },
    formatGraphData (data, isMatched = false, isCitations = false) {
      if (!data) {
        return []
      }

      const items = data
        .sort((a, b) => b.year - a.year)
        .reduce((a, b, i, arr) => {
          // If there is no year, or year is lower than minimum year, just go to the next value
          if (!b.year) return a

          const prevYear = i && arr[i - 1].year
          // Check if there are any gaps between years, add missing dataPoints if needed
          if (prevYear && prevYear < b.year - 1) {
            const count = b.year - prevYear - 1
            for (let j = 0; j < count; j++) {
              a.push({
                x: prevYear + j + 1,
                y: 0
              })
            }
          }

          if (isMatched) {
            isCitations ? this.citationsMatched += b.count : this.publicationsMatched += b.count
          }

          a.push({
            x: b.year,
            y: isMatched ? b.count : b.total
          })
          return a
        }, [])

      return items.map(i => {
        return {
          ...i,
          year: i.x.toString()
        }
      }).slice(0, Math.min(items.length, this.yearsToShow))
    }
  }
}
</script>
